import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import {
    Activity,
    Calendar,
    Filter,
    CheckCircle2,
    XCircle,
    Clock,
    Unlock,
    ChevronLeft,
    ChevronRight,
    Mail,
    Plus,
    Minus,
    Search,
    RefreshCw,
    Lock,
    ShieldAlert,
    RotateCcw,
    ArrowRight
} from 'lucide-react';
import UnlockConfirmModal from '../components/UnlockConfirmModal';

const REPORT_NAME_MAP = {
    raw_transitions: 'Ham Geçişler Raporu',
    entry_exit_summary: 'Giriş-Çıkış Özet Raporu',
    puantaj_raporu: 'Puantaj Raporu',
    daily_attendance: 'Günlük Devam Raporu',
    latecomers_report: 'Geç Gelenler Raporu',
    early_leavers_report: 'Erken Çıkanlar Raporu',
    absent_employees: 'Devamsızlar Raporu',
    leave_patients_report: 'İzinliler Raporu',
    overtime_report: 'Fazla Mesai Raporu',
    manual_interventions: 'Manuel Müdahaleler Raporu',
    card_forgotten: 'Kart Unutanlar Raporu'
};

const SETTING_KEY_LABELS = {
    'Auth.LoginIdentifierMode': 'Personel Sisteme Giriş Kullanıcı Adı Politikası',
    'Mobile.AutoApproveFirstDevice': 'Yeni Cihaz Otomatik Onay Politikası',
    'Mobile.AllowMultipleDevices': 'Çoklu Mobil Cihaz Politikası',
    'Mobile.QrPassMode': 'Mobil QR Geçiş Modu',
    'DeclaredPass.Enabled': 'Mobil Beyanlı Geçiş Özelliği',
    'DeclaredPass.RequireLocation': 'Beyanlı Geçiş GPS Konum Zorunluluğu'
};

const PLAIN_TEXT_TRANSLATIONS = {
    'Wrong password': 'Hatalı şifre denemesi (Web Panel)',
    'Wrong password (mobile)': 'Hatalı şifre denemesi (Mobil Uygulama)',
    'Max failed attempts reached': 'Maksimum hatalı giriş sınırına ulaşıldı — Hesap kilitlendi',
    'Max failed attempts reached (mobile)': 'Maksimum hatalı giriş sınırına ulaşıldı (Mobil) — Hesap kilitlendi',
    'Locked out': 'Kilitli hesaba giriş denemesi reddedildi',
    'Locked out (mobile)': 'Kilitli hesaba mobil giriş denemesi reddedildi',
    'User not found or deleted': 'Kullanıcı bulunamadı veya silinmiş hesap',
    'User not found or deleted (mobile)': 'Kullanıcı bulunamadı veya silinmiş hesap (Mobil)',
    'User is inactive': 'Pasif kullanıcı hesabı ile giriş denemesi',
    'User is inactive (mobile)': 'Pasif kullanıcı hesabı ile mobil giriş denemesi',
    'Temporary password expired': 'Geçici şifrenin geçerlilik süresi dolmuş',
    'Mobile login': 'Mobil uygulama üzerinden başarılı oturum açıldı',
    'Current password incorrect': 'Şifre değiştirme sırasında mevcut şifre hatalı girildi',
    'Invalid TOTP code': 'Geçersiz 6 haneli MFA (Authenticator) doğrulama kodu',
    'Invalid TOTP code during setup': 'MFA kurulumu sırasında geçersiz doğrulama kodu girildi',
    'Display method used': 'Yeni geçici şifre yönetici ekranında görüntülendi',
    'Out of scope': 'Yetki kapsamı dışındaki personel için şifre sıfırlama denemesi'
};

const EVENT_OPTIONS = [
    { value: '', label: 'Tüm Olaylar' },
    // Giriş & Oturum
    { value: 'LOGIN_SUCCESS', label: 'Giriş Başarılı' },
    { value: 'LOGIN_FAIL', label: 'Giriş Hatalı' },
    { value: 'LOCKOUT', label: 'Hesap Kilitleme (Ban)' },
    { value: 'UNBAN_BY_TENANT_ADMIN', label: 'Yönetici Tarafından Kilit Açıldı' },
    { value: 'UNBAN_BY_SUPERADMIN', label: 'Sistem Yöneticisi Tarafından Kilit Açıldı' },
    // İki Adımlı Doğrulama (MFA)
    { value: 'MFA_LOGIN_SUCCESS', label: 'MFA Doğrulama Başarılı' },
    { value: 'MFA_LOGIN_FAIL', label: 'MFA Doğrulama Hatalı' },
    { value: 'MFA_ENABLED', label: 'Kullanıcı MFA Aktifleştirdi' },
    { value: 'MFA_DISABLED', label: 'Kullanıcı MFA Devre Dışı Bıraktı' },
    { value: 'MFA_SETUP_FAIL', label: 'MFA Kurulum Kodu Hatalı' },
    { value: 'MFA_POLICY_UPDATED', label: 'Firma MFA Politikası Güncellendi' },
    { value: 'ROLE_MFA_REQUIREMENT_UPDATED', label: 'Rol MFA Zorunluluğu Güncellendi' },
    // Şifre İşlemleri
    { value: 'PASSWORD_RESET', label: 'Şifre Sıfırlama (Yönetici)' },
    { value: 'RESET_EMAIL_SENT', label: 'Şifre Sıfırlama E-postası Gönderildi' },
    { value: 'PASSWORD_CHANGED', label: 'Şifre Değiştirildi' },
    { value: 'PASSWORD_CHANGE_SUCCESS', label: 'Şifre Değişimi Başarılı (Profil)' },
    { value: 'PASSWORD_CHANGE_FAIL', label: 'Şifre Değişimi Hatalı' },
    // Sistem, Politika ve Puantaj Ayarları
    { value: 'FIRM_SETTING_UPDATED', label: 'Firma / Mobil Politika Ayarı Güncellendi' },
    { value: 'PAYROLL_CALC_SETTINGS_UPDATED', label: 'Otomatik Puantaj Ayarları Güncellendi' },
    { value: 'PAYROLL_MANUAL_RUN_TRIGGERED', label: 'Puantaj Hesaplama Manuel Başlatıldı' },
    { value: 'PAYROLL_STUCK_JOBS_RESET', label: 'Askıdaki Puantaj İşlemi Sıfırlandı' },
    { value: 'FIRM_TOKEN_INCONSISTENT', label: 'Firma Token Uyuşmazlığı' },
    { value: 'SUB_COMPANY_UNAUTHORIZED_ACCESS', label: 'Yetkisiz Alt Firma Erişim Denemesi' },
    { value: 'FIRM_MISMATCH', label: 'Firma Bağlam Uyuşmazlığı' },
    // Mail Sistem & Abonelik
    { value: 'MAIL_SUB_CREATED', label: 'Mail Aboneliği Oluşturuldu' },
    { value: 'MAIL_SUB_UPDATED', label: 'Mail Aboneliği Güncellendi' },
    { value: 'MAIL_SUB_DELETED', label: 'Mail Aboneliği Silindi' },
    { value: 'MAIL_SUB_RUN_NOW', label: 'Mail Aboneliği Manuel Gönderildi' },
    { value: 'MAIL_SETTINGS_UPDATED', label: 'Mail SMTP Ayarları Güncellendi' },
    { value: 'MAIL_SETTINGS_TESTED', label: 'Mail SMTP Bağlantı Testi' },
    { value: 'MAIL_SYSTEM_TOGGLED', label: 'Otomatik Mail Sistemi Değiştirildi' },
    // İzin & Bakiye
    { value: 'LEAVE_AUTO_SETTINGS_UPDATED', label: 'Yıllık İzin Hakediş Ayarları Güncellendi' },
    { value: 'LEAVE_AUTO_ENTITLEMENT_TRIGGERED', label: 'Yıllık İzin Hakediş Motoru Çalıştırıldı' },
    { value: 'LEAVE_MANUAL_MOVEMENT_CREATED', label: 'Manuel İzin Bakiyesi Hareketi Eklendi' },
    { value: 'LEAVE_MANUAL_MOVEMENT_DELETED', label: 'Manuel İzin Bakiyesi Hareketi Silindi' },
    { value: 'LEAVE_BALANCE_EXCEL_IMPORTED', label: 'Excel ile Toplu İzin Bakiyesi Aktarımı' },
    { value: 'LEAVE_OVERRIDE_APPROVED', label: 'İzin Çakışma / Override Onayı Verildi' },
    { value: 'LEAVE_CANCELLED', label: 'İzin Talebi İptal Edildi' },
    { value: 'LEAVE_CANCELLED_FROM_QUALITY', label: 'Puantaj Kalite: İzin İptali / Günü Düşme' },
    // Puantaj Kalite & Müdahaleler
    { value: 'PUANTAJ_SUGGESTION_APPLIED', label: 'Puantaj Kalite Önerisi Uygulandı' },
    { value: 'PUANTAJ_MANUAL_PASS_ADDED', label: 'Puantaj Kalite Manuel Geçiş Eklendi' },
    { value: 'PUANTAJ_PASS_DIRECTION_CHANGED', label: 'Geçiş Yönü Değiştirildi (Giriş ⇄ Çıkış)' },
    { value: 'PUANTAJ_PASS_IGNORED', label: 'Geçiş Kaydı Yok Sayıldı' },
    { value: 'PUANTAJ_PASS_RESTORED', label: 'Yok Sayılan Geçiş Kaydı Geri Yüklendi' },
    { value: 'PUANTAJ_QUALITY_ACCEPTED', label: 'Puantaj Kalite Durumu Kabul Edildi' },
    { value: 'PUANTAJ_QUALITY_REVOKE_ACCEPT', label: 'Puantaj Kalite Kabulü Kaldırıldı' },
    { value: 'PUANTAJ_BATCH_QUALITY_APPLIED', label: 'Toplu Puantaj Kalite Düzeltmesi Uygulandı' },
    { value: 'PAYROLL_DYNAMIC_WEEKOFF_OVERRIDE', label: 'Dinamik Hafta Tatili Manuel Sabitleme' },
    // Bölüm, Pozisyon, Lokasyon & Veri Kapsamı
    { value: 'DEPARTMENT_CREATED', label: 'Bölüm Oluşturuldu' },
    { value: 'DEPARTMENT_UPDATED', label: 'Bölüm Güncellendi' },
    { value: 'DEPARTMENT_TRANSFERRED', label: 'Bölüm Personel & Yetki Aktarımı' },
    { value: 'DEPARTMENT_DELETED', label: 'Bölüm Silindi' },
    { value: 'POSITION_CREATED', label: 'Pozisyon Oluşturuldu' },
    { value: 'POSITION_UPDATED', label: 'Pozisyon Güncellendi' },
    { value: 'POSITION_TRANSFERRED', label: 'Pozisyon Personel Aktarımı' },
    { value: 'POSITION_DELETED', label: 'Pozisyon Silindi' },
    { value: 'LOCATION_CREATED', label: 'Lokasyon Oluşturuldu' },
    { value: 'LOCATION_UPDATED', label: 'Lokasyon Güncellendi' },
    { value: 'LOCATION_TRANSFERRED', label: 'Lokasyon Personel & Yetki Aktarımı' },
    { value: 'LOCATION_DELETED', label: 'Lokasyon Silindi' },
    { value: 'WORKING_REGIME_CREATED', label: 'Çalışma Rejimi Oluşturuldu' },
    { value: 'WORKING_REGIME_UPDATED', label: 'Çalışma Rejimi Güncellendi' },
    { value: 'WORKING_REGIME_SCHEDULES_SYNCED', label: 'Çalışma Rejimi Vardiya Senkronizasyonu' },
    { value: 'WORKING_REGIME_EMPLOYEES_TRANSFERRED', label: 'Çalışma Rejimi Personel Aktarımı' },
    { value: 'WORKING_REGIME_TRANSFERRED_AND_DELETED', label: 'Çalışma Rejimi Aktarılıp Silindi' },
    { value: 'WORKING_REGIME_DELETED', label: 'Çalışma Rejimi Silindi' },
    { value: 'SHIFT_ASSIGNMENT_CREATE', label: 'Personel Vardiya Ataması' },
    { value: 'SHIFT_ASSIGNMENT_BULK_CREATE', label: 'Toplu Personel Vardiya Ataması' },
    { value: 'SHIFT_ASSIGNMENT_DELETE', label: 'Personel Vardiya Ataması Silindi' },
    { value: 'USER_DATASCOPE_UPDATED', label: 'Personel Veri Görme Kapsamı Güncellendi' },
    // Personel & Kimlik Yönetimi
    { value: 'EMPLOYEE_CREATED', label: 'Yeni Personel Oluşturuldu' },
    { value: 'EMPLOYEE_UPDATED', label: 'Personel Bilgileri Güncellendi' },
    { value: 'EMPLOYEE_DELETED', label: 'Personel Silindi' },
    { value: 'EMPLOYEE_CREDENTIAL_ADDED', label: 'Personel Kart / Kimlik Tanımlandı' },
    { value: 'EMPLOYEE_CREDENTIAL_DELETED', label: 'Personel Kart / Kimlik Silindi' }
];

const SecurityAuditLogsPage = () => {
    const { permissions } = useAuth();
    const hasUnlockPermission =
        permissions?.includes('security:audit:unlock') ||
        permissions?.includes('settings:manage');

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [summary, setSummary] = useState({
        totalCount: 0,
        successCount: 0,
        failedCount: 0,
        lockoutCount: 0,
        activeLockedUsersCount: 0
    });
    const [selectedUserForUnlock, setSelectedUserForUnlock] = useState(null);

    const [filters, setFilters] = useState({
        eventType: '',
        startDate: '',
        endDate: '',
        search: '',
        isSuccess: ''
    });
    const [searchInput, setSearchInput] = useState('');

    // Arama kutusu için kısa debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(prev => {
                if (prev.search === searchInput) return prev;
                setPage(1);
                return { ...prev, search: searchInput };
            });
        }, 350);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                pageSize: '20'
            });
            if (filters.eventType) params.set('eventType', filters.eventType);
            if (filters.startDate) params.set('startDate', filters.startDate);
            if (filters.endDate) params.set('endDate', filters.endDate);
            if (filters.search) params.set('search', filters.search);
            if (filters.isSuccess !== '') params.set('isSuccess', filters.isSuccess);

            const response = await apiClient(`SecurityAuditLogs?${params.toString()}`);
            setLogs(response.data || []);
            setTotalCount(response.totalCount || 0);
            if (response.summary) {
                setSummary(response.summary);
            }
        } catch (err) {
            console.error('Loglar yüklenirken hata:', err);
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const updateFilter = (key, value) => {
        setPage(1);
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setPage(1);
        setSearchInput('');
        setFilters({
            eventType: '',
            startDate: '',
            endDate: '',
            search: '',
            isSuccess: ''
        });
    };

    const applyQuickDateRange = (days) => {
        const end = new Date();
        const start = new Date();
        if (days > 0) {
            start.setDate(end.getDate() - (days - 1));
        }
        const fmt = (d) => d.toISOString().split('T')[0];
        setPage(1);
        setFilters(prev => ({
            ...prev,
            startDate: fmt(start),
            endDate: fmt(end)
        }));
    };

    const getEventBadge = (type) => {
        switch (type) {
            case 'LOGIN_SUCCESS':
            case 'MFA_LOGIN_SUCCESS':
            case 'PASSWORD_CHANGED':
            case 'PASSWORD_CHANGE_SUCCESS':
            case 'MFA_ENABLED':
            case 'MAIL_SUB_CREATED':
            case 'DEPARTMENT_CREATED':
            case 'POSITION_CREATED':
            case 'LOCATION_CREATED':
            case 'WORKING_REGIME_CREATED':
            case 'EMPLOYEE_CREATED':
            case 'EMPLOYEE_CREDENTIAL_ADDED':
                return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30';
            case 'LOGIN_FAIL':
            case 'MFA_LOGIN_FAIL':
            case 'MFA_SETUP_FAIL':
            case 'PASSWORD_CHANGE_FAIL':
            case 'MAIL_SUB_DELETED':
            case 'LEAVE_MANUAL_MOVEMENT_DELETED':
            case 'LEAVE_CANCELLED':
            case 'DEPARTMENT_DELETED':
            case 'POSITION_DELETED':
            case 'LOCATION_DELETED':
            case 'WORKING_REGIME_DELETED':
            case 'WORKING_REGIME_TRANSFERRED_AND_DELETED':
            case 'EMPLOYEE_DELETED':
            case 'EMPLOYEE_CREDENTIAL_DELETED':
            case 'FIRM_TOKEN_INCONSISTENT':
            case 'SUB_COMPANY_UNAUTHORIZED_ACCESS':
            case 'FIRM_MISMATCH':
                return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30';
            case 'LOCKOUT':
                return 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-300 dark:border-purple-500/30';
            case 'UNBAN_BY_SUPERADMIN':
            case 'UNBAN_BY_TENANT_ADMIN':
            case 'MAIL_SUB_RUN_NOW':
            case 'PAYROLL_MANUAL_RUN_TRIGGERED':
            case 'DEPARTMENT_TRANSFERRED':
            case 'POSITION_TRANSFERRED':
            case 'LOCATION_TRANSFERRED':
            case 'WORKING_REGIME_EMPLOYEES_TRANSFERRED':
            case 'WORKING_REGIME_SCHEDULES_SYNCED':
            case 'SHIFT_ASSIGNMENT_CREATE':
            case 'SHIFT_ASSIGNMENT_BULK_CREATE':
                return 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:border-teal-500/30';
            case 'PASSWORD_RESET':
            case 'RESET_EMAIL_SENT':
            case 'MAIL_SUB_UPDATED':
            case 'LEAVE_BALANCE_EXCEL_IMPORTED':
            case 'LEAVE_AUTO_ENTITLEMENT_TRIGGERED':
            case 'LEAVE_AUTO_SETTINGS_UPDATED':
            case 'LEAVE_MANUAL_MOVEMENT_CREATED':
            case 'LEAVE_OVERRIDE_APPROVED':
            case 'USER_DATASCOPE_UPDATED':
            case 'EMPLOYEE_UPDATED':
                return 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/30';
            case 'MAIL_SETTINGS_UPDATED':
            case 'MAIL_SETTINGS_TESTED':
            case 'MAIL_SYSTEM_TOGGLED':
            case 'FIRM_SETTING_UPDATED':
            case 'MFA_POLICY_UPDATED':
            case 'ROLE_MFA_REQUIREMENT_UPDATED':
            case 'MFA_DISABLED':
            case 'PAYROLL_CALC_SETTINGS_UPDATED':
            case 'PAYROLL_STUCK_JOBS_RESET':
            case 'DEPARTMENT_UPDATED':
            case 'POSITION_UPDATED':
            case 'LOCATION_UPDATED':
                return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30';
            default:
                return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
        }
    };

    const getEventLabel = (type) => {
        const option = EVENT_OPTIONS.find(o => o.value === type);
        return option ? option.label : type;
    };

    const renderLogDetails = (log) => {
        if (!log.detailsJson) {
            if (log.eventType === 'LOGIN_SUCCESS') return <span className="text-gray-500 dark:text-gray-400">Web panel üzerinden başarılı oturum açıldı</span>;
            if (log.eventType === 'PASSWORD_CHANGE_SUCCESS') return <span className="text-gray-500 dark:text-gray-400">Kullanıcı kendi şifresini güncelledi</span>;
            if (log.eventType === 'MFA_ENABLED') return <span className="text-gray-500 dark:text-gray-400">İki adımlı doğrulama (Authenticator) kurulumu tamamlandı</span>;
            if (log.eventType === 'MFA_DISABLED') return <span className="text-gray-500 dark:text-gray-400">İki adımlı doğrulama devre dışı bırakıldı</span>;
            if (log.eventType === 'MFA_LOGIN_SUCCESS') return <span className="text-gray-500 dark:text-gray-400">6 haneli MFA kodu doğrulandı</span>;
            if (log.eventType === 'RESET_EMAIL_SENT') return <span className="text-gray-500 dark:text-gray-400">Şifre sıfırlama bağlantısı/bilgisi e-posta ile iletildi</span>;
            return '-';
        }

        // Önce düz metin çevirisi var mı kontrol et
        const trimmed = String(log.detailsJson).trim();
        if (PLAIN_TEXT_TRANSLATIONS[trimmed]) {
            return (
                <span className={log.isSuccess ? 'text-gray-700 dark:text-gray-300' : 'text-rose-600 dark:text-rose-400 font-medium'}>
                    {PLAIN_TEXT_TRANSLATIONS[trimmed]}
                </span>
            );
        }

        try {
            const parsed = JSON.parse(log.detailsJson);
            const isMailEvent = String(log.eventType || '').startsWith('MAIL_');

            if (isMailEvent) {
                const addedTo = Array.isArray(parsed.AddedTo) ? parsed.AddedTo : [];
                const removedTo = Array.isArray(parsed.RemovedTo) ? parsed.RemovedTo : [];
                const addedCc = Array.isArray(parsed.AddedCc) ? parsed.AddedCc : [];
                const removedCc = Array.isArray(parsed.RemovedCc) ? parsed.RemovedCc : [];
                const currentTo = Array.isArray(parsed.CurrentTo) ? parsed.CurrentTo : [];
                const reportLabel = parsed.ReportType ? (REPORT_NAME_MAP[parsed.ReportType] || parsed.ReportType) : null;

                return (
                    <div className="space-y-1.5 text-xs max-w-md">
                        {parsed.Action && (
                            <div className="font-semibold text-gray-800 dark:text-gray-100">
                                {parsed.Action}
                            </div>
                        )}
                        {(reportLabel || parsed.Schedule) && (
                            <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                                {reportLabel && (
                                    <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-medium">
                                        Rapor: {reportLabel}
                                    </span>
                                )}
                                {parsed.Schedule && (
                                    <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                                        {parsed.Schedule}
                                    </span>
                                )}
                            </div>
                        )}
                        {addedTo.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1">
                                <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                                    <Plus className="w-3 h-3" /> Eklenen (To):
                                </span>
                                {addedTo.map((m) => (
                                    <span key={m} className="px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 font-mono text-[11px]">
                                        {m}
                                    </span>
                                ))}
                            </div>
                        )}
                        {removedTo.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1">
                                <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-rose-600 dark:text-rose-400">
                                    <Minus className="w-3 h-3" /> Çıkarılan (To):
                                </span>
                                {removedTo.map((m) => (
                                    <span key={m} className="px-1.5 py-0.5 rounded bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 font-mono text-[11px] line-through">
                                        {m}
                                    </span>
                                ))}
                            </div>
                        )}
                        {addedCc.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1">
                                <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400">
                                    <Plus className="w-3 h-3" /> Eklenen (Cc):
                                </span>
                                {addedCc.map((m) => (
                                    <span key={m} className="px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 font-mono text-[11px]">
                                        {m}
                                    </span>
                                ))}
                            </div>
                        )}
                        {removedCc.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1">
                                <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-rose-600 dark:text-rose-400">
                                    <Minus className="w-3 h-3" /> Çıkarılan (Cc):
                                </span>
                                {removedCc.map((m) => (
                                    <span key={m} className="px-1.5 py-0.5 rounded bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 font-mono text-[11px] line-through">
                                        {m}
                                    </span>
                                ))}
                            </div>
                        )}
                        {addedTo.length === 0 && removedTo.length === 0 && currentTo.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1">
                                <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                                    <Mail className="w-3 h-3" /> Alıcılar:
                                </span>
                                {currentTo.slice(0, 5).map((m) => (
                                    <span key={m} className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-mono text-[11px]">
                                        {m}
                                    </span>
                                ))}
                                {currentTo.length > 5 && (
                                    <span className="text-[11px] text-gray-500">+{currentTo.length - 5} diğer</span>
                                )}
                            </div>
                        )}
                        {Array.isArray(parsed.Changes) && parsed.Changes.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1 pt-0.5">
                                {parsed.Changes.map((chg, idx) => (
                                    <span
                                        key={idx}
                                        className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800 text-[11px]"
                                    >
                                        {chg}
                                    </span>
                                ))}
                            </div>
                        )}
                        {parsed.Error && (
                            <div className="text-[11px] text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded px-2 py-1">
                                {parsed.Error}
                            </div>
                        )}
                    </div>
                );
            }

            // Genel / Mobil Politika Ayar Değişikliği (FIRM_SETTING_UPDATED)
            if (log.eventType === 'FIRM_SETTING_UPDATED' || parsed.Key) {
                const label = SETTING_KEY_LABELS[parsed.Key] || parsed.Key;
                return (
                    <div className="space-y-1 text-xs">
                        <span className="font-semibold text-gray-800 dark:text-gray-200 block">
                            {label}
                        </span>
                        <div className="inline-flex items-center gap-1.5 font-mono text-[11px]">
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                                {String(parsed.OldValue ?? 'Yok')}
                            </span>
                            <ArrowRight className="w-3 h-3 text-gray-400" />
                            <span className="px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-bold">
                                {String(parsed.NewValue)}
                            </span>
                            {parsed.DeactivatedExtraDevices > 0 && (
                                <span className="px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300">
                                    ({parsed.DeactivatedExtraDevices} eski cihaz pasife alındı)
                                </span>
                            )}
                            {parsed.SyncedUsers > 0 && (
                                <span className="px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 font-sans font-semibold">
                                    ({parsed.SyncedUsers} personelin kullanıcı adı senkronize edildi)
                                </span>
                            )}
                        </div>
                    </div>
                );
            }

            const isLeaveEvent = String(log.eventType || '').startsWith('LEAVE_');
            if (isLeaveEvent) {
                return (
                    <div className="space-y-1 text-xs max-w-md">
                        {(parsed.action || parsed.Action) && (
                            <div className="font-semibold text-gray-800 dark:text-gray-100">
                                {parsed.action || parsed.Action}
                            </div>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                            {parsed.originalFileName && (
                                <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-mono">
                                    Dosya: {parsed.originalFileName}
                                </span>
                            )}
                            {parsed.excelBaseDate && (
                                <span className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 font-semibold">
                                    Baz Tarih: {parsed.excelBaseDate}
                                </span>
                            )}
                            {parsed.successCount !== undefined && (
                                <span className="px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-semibold">
                                    Başarılı: {parsed.successCount} / Hatalı: {parsed.failedCount ?? 0}
                                </span>
                            )}
                            {parsed.employeeName && (
                                <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-medium">
                                    Personel: {parsed.employeeName}
                                </span>
                            )}
                            {parsed.netGun !== undefined && (
                                <span className="px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-bold">
                                    {parsed.netGun >= 0 ? `+${parsed.netGun}` : parsed.netGun} gün
                                </span>
                            )}
                            {parsed.grantedCount !== undefined && (
                                <span className="px-2 py-0.5 rounded bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-semibold">
                                    +{parsed.grantedCount} dönem ({parsed.totalGrantedDays ?? 0} gün)
                                </span>
                            )}
                        </div>
                    </div>
                );
            }

            const isOrgOrScopeEvent =
                String(log.eventType || '').startsWith('DEPARTMENT_') ||
                String(log.eventType || '').startsWith('POSITION_') ||
                String(log.eventType || '').startsWith('LOCATION_') ||
                String(log.eventType || '').startsWith('EMPLOYEE_') ||
                log.eventType === 'USER_DATASCOPE_UPDATED';

            if (isOrgOrScopeEvent) {
                const changes = Array.isArray(parsed.Changes) ? parsed.Changes : [];
                const depts = Array.isArray(parsed.Departments) ? parsed.Departments : [];
                const locs = Array.isArray(parsed.Locations) ? parsed.Locations : [];

                return (
                    <div className="space-y-1.5 text-xs max-w-lg">
                        {(parsed.Action || parsed.action) && (
                            <div className="font-semibold text-gray-800 dark:text-gray-100">
                                {parsed.Action || parsed.action}
                            </div>
                        )}

                        {parsed.SourceName && parsed.TargetName && (
                            <div className="inline-flex items-center gap-1.5 text-[11px] font-medium">
                                <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                                    {parsed.SourceName}
                                </span>
                                <ArrowRight className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 font-semibold">
                                    {parsed.TargetName}
                                </span>
                            </div>
                        )}

                        {parsed.OldScope && parsed.NewScope && (
                            <div className="inline-flex items-center gap-1.5 text-[11px] font-medium">
                                <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                                    {parsed.OldScope}
                                </span>
                                <ArrowRight className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-bold">
                                    {parsed.NewScope}
                                </span>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                            {parsed.SicilNo && (
                                <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 font-mono font-semibold">
                                    Sicil: {parsed.SicilNo}
                                </span>
                            )}
                            {parsed.Username && (
                                <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 font-mono">
                                    Kullanıcı Adı: {parsed.Username}
                                </span>
                            )}
                            {parsed.CardNo && (
                                <span className="px-2 py-0.5 rounded bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800 font-mono font-semibold">
                                    Kart: {parsed.CardNo}
                                </span>
                            )}
                            {parsed.CredentialType && (
                                <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                                    Tip: {parsed.CredentialType}
                                </span>
                            )}
                            {parsed.OldSicilNo && parsed.NewSicilNo && parsed.OldSicilNo !== parsed.NewSicilNo && (
                                <span className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800 font-mono font-semibold">
                                    Sicil Değişimi: {parsed.OldSicilNo} → {parsed.NewSicilNo}
                                </span>
                            )}
                            {parsed.TransferredEmployeeCount > 0 && (
                                <span className="px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 font-semibold">
                                    {parsed.TransferredEmployeeCount} personel aktarıldı
                                </span>
                            )}
                            {parsed.ClearedEmployeeCount > 0 && (
                                <span className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800 font-semibold">
                                    {parsed.ClearedEmployeeCount} personelin lokasyonu kaldırıldı
                                </span>
                            )}
                            {parsed.TransferredScopeUserCount > 0 && (
                                <span className="px-2 py-0.5 rounded bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 font-semibold">
                                    {parsed.TransferredScopeUserCount} kullanıcı veri kapsamı taşındı
                                </span>
                            )}
                            {parsed.Status && (
                                <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                                    Durum: {parsed.Status}
                                </span>
                            )}
                            {changes.map((chg, idx) => (
                                <span
                                    key={idx}
                                    className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800"
                                >
                                    {chg}
                                </span>
                            ))}
                        </div>

                        {depts.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1 text-[11px]">
                                <span className="text-gray-500 dark:text-gray-400 font-medium">Bölümler:</span>
                                {depts.map((d) => (
                                    <span key={d} className="px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                                        {d}
                                    </span>
                                ))}
                            </div>
                        )}

                        {locs.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1 text-[11px]">
                                <span className="text-gray-500 dark:text-gray-400 font-medium">Lokasyonlar:</span>
                                {locs.map((l) => (
                                    <span key={l} className="px-1.5 py-0.5 rounded bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800">
                                        {l}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                );
            }

            if (parsed.Action || parsed.action) {
                return (
                    <span className="font-medium text-gray-800 dark:text-gray-200">
                        {parsed.Action || parsed.action}
                    </span>
                );
            }

            return (
                <span className="font-mono text-[11px] text-gray-600 dark:text-gray-400">
                    {log.detailsJson}
                </span>
            );
        } catch {
            return (
                <span className="text-gray-700 dark:text-gray-300">
                    {PLAIN_TEXT_TRANSLATIONS[trimmed] || log.detailsJson}
                </span>
            );
        }
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / 20));
    const hasActiveFilters = Boolean(
        filters.eventType || filters.startDate || filters.endDate || filters.search || filters.isSuccess !== ''
    );

    return (
        <div className="space-y-5">
            {/* Üst Başlık ve KPI Kartları */}
            <div className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-3.5">
                        <div className="w-11 h-11 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200/70 dark:border-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                            <Activity className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Güvenlik Denetim Logları (Audit Trail)
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Oturum açma hareketlerini, 2FA (MFA) doğrulamalarını, şifre sıfırlamalarını, kilitlenen hesapları ve sistem politikası değişikliklerini izleyin.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={fetchLogs}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-gray-100 dark:bg-[#111214] text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                            Yenile
                        </button>
                    </div>
                </div>

                {/* 4'lü KPI ve Hızlı Filtre Kartları */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 mt-5">
                    <button
                        type="button"
                        onClick={() => {
                            setPage(1);
                            setFilters(prev => ({ ...prev, isSuccess: '', eventType: '' }));
                        }}
                        className={`p-3.5 rounded-xl border text-left transition-all ${
                            filters.isSuccess === '' && !filters.eventType
                                ? 'bg-indigo-50/70 dark:bg-indigo-500/10 border-indigo-400 dark:border-indigo-500/50 ring-1 ring-indigo-500/20'
                                : 'bg-gray-50/80 dark:bg-[#111214] border-gray-200/80 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                        }`}
                    >
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Toplam Denetim Kaydı</span>
                            <Activity className="w-4 h-4 text-indigo-500" />
                        </div>
                        <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                            {summary.totalCount}
                        </p>
                    </button>

                    <button
                        type="button"
                        onClick={() => updateFilter('isSuccess', filters.isSuccess === 'true' ? '' : 'true')}
                        className={`p-3.5 rounded-xl border text-left transition-all ${
                            filters.isSuccess === 'true'
                                ? 'bg-emerald-50/70 dark:bg-emerald-500/10 border-emerald-400 dark:border-emerald-500/50 ring-1 ring-emerald-500/20'
                                : 'bg-gray-50/80 dark:bg-[#111214] border-gray-200/80 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                        }`}
                    >
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Başarılı İşlemler</span>
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        </div>
                        <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                            {summary.successCount}
                        </p>
                    </button>

                    <button
                        type="button"
                        onClick={() => updateFilter('isSuccess', filters.isSuccess === 'false' ? '' : 'false')}
                        className={`p-3.5 rounded-xl border text-left transition-all ${
                            filters.isSuccess === 'false'
                                ? 'bg-rose-50/70 dark:bg-rose-500/10 border-rose-400 dark:border-rose-500/50 ring-1 ring-rose-500/20'
                                : 'bg-gray-50/80 dark:bg-[#111214] border-gray-200/80 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                        }`}
                    >
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Hatalı / Reddedilen</span>
                            <ShieldAlert className="w-4 h-4 text-rose-500" />
                        </div>
                        <p className="text-xl font-bold text-rose-600 dark:text-rose-400 mt-1">
                            {summary.failedCount}
                        </p>
                    </button>

                    <button
                        type="button"
                        onClick={() => updateFilter('eventType', filters.eventType === 'LOCKOUT' ? '' : 'LOCKOUT')}
                        className={`p-3.5 rounded-xl border text-left transition-all ${
                            filters.eventType === 'LOCKOUT'
                                ? 'bg-purple-50/70 dark:bg-purple-500/10 border-purple-400 dark:border-purple-500/50 ring-1 ring-purple-500/20'
                                : 'bg-gray-50/80 dark:bg-[#111214] border-gray-200/80 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                        }`}
                    >
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Hesap Kilitleme (Ban)</span>
                            <Lock className="w-4 h-4 text-purple-500" />
                        </div>
                        <div className="flex items-baseline gap-2 mt-1">
                            <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                                {summary.lockoutCount}
                            </p>
                            {summary.activeLockedUsersCount > 0 && (
                                <span className="text-[11px] font-semibold text-rose-600 dark:text-rose-400">
                                    ({summary.activeLockedUsersCount} şu an kilitli)
                                </span>
                            )}
                        </div>
                    </button>
                </div>
            </div>

            {/* Gelişmiş Filtre Çubuğu */}
            <div className="bg-white dark:bg-[#18191c] p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {/* Kullanıcı / IP Arama */}
                    <div className="lg:col-span-1">
                        <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Kullanıcı / IP / Detay Ara
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Kullanıcı adı, IP, olay..."
                                className="w-full pl-9 pr-3 py-2 text-xs bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>

                    {/* Olay Tipi */}
                    <div>
                        <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Olay Tipi
                        </label>
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <select
                                value={filters.eventType}
                                onChange={(e) => updateFilter('eventType', e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-xs bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 dark:text-white"
                            >
                                {EVENT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Durum Filtresi */}
                    <div>
                        <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            İşlem Sonucu
                        </label>
                        <select
                            value={filters.isSuccess}
                            onChange={(e) => updateFilter('isSuccess', e.target.value)}
                            className="w-full px-3 py-2 text-xs bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 dark:text-white"
                        >
                            <option value="">Tümü (Başarılı + Hatalı)</option>
                            <option value="true">Yalnızca Başarılı</option>
                            <option value="false">Yalnızca Hatalı / Reddedilen</option>
                        </select>
                    </div>

                    {/* Başlangıç Tarihi */}
                    <div>
                        <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Başlangıç Tarihi
                        </label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => updateFilter('startDate', e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-xs bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>

                    {/* Bitiş Tarihi */}
                    <div>
                        <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Bitiş Tarihi (Gün Sonu Dahil)
                        </label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => updateFilter('endDate', e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-xs bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>
                </div>

                {/* Hızlı Tarih Kısayolları ve Filtre Temizleme */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] text-gray-400 mr-1">Hızlı Tarih:</span>
                        <button
                            type="button"
                            onClick={() => applyQuickDateRange(1)}
                            className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-gray-100 dark:bg-[#111214] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors"
                        >
                            Bugün
                        </button>
                        <button
                            type="button"
                            onClick={() => applyQuickDateRange(7)}
                            className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-gray-100 dark:bg-[#111214] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors"
                        >
                            Son 7 Gün
                        </button>
                        <button
                            type="button"
                            onClick={() => applyQuickDateRange(30)}
                            className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-gray-100 dark:bg-[#111214] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors"
                        >
                            Son 30 Gün
                        </button>
                    </div>

                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={clearFilters}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:underline"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Filtreleri Temizle
                        </button>
                    )}
                </div>
            </div>

            {/* Tablo */}
            <div className="bg-white dark:bg-[#18191c] rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-gray-50/80 dark:bg-[#111214] border-b border-gray-200 dark:border-gray-800">
                                <th className="px-5 py-3.5 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tarih (TR)</th>
                                <th className="px-5 py-3.5 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Olay Tipi</th>
                                <th className="px-5 py-3.5 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hedef / İşlemi Yapan</th>
                                <th className="px-5 py-3.5 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">IP & İstemci</th>
                                <th className="px-5 py-3.5 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Detaylar</th>
                                <th className="px-5 py-3.5 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Durum</th>
                                {hasUnlockPermission && (
                                    <th className="px-5 py-3.5 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Aksiyon</th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan={hasUnlockPermission ? 7 : 6} className="px-5 py-4">
                                            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded-md w-3/4"></div>
                                        </td>
                                    </tr>
                                ))
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan={hasUnlockPermission ? 7 : 6} className="px-5 py-12 text-center text-xs text-gray-500 dark:text-gray-400">
                                        Seçili kriterlere uygun güvenlik denetim kaydı bulunamadı.
                                    </td>
                                </tr>
                            ) : (
                                logs.map(log => {
                                    const targetDisplay = log.targetFullName || log.targetUsername || '-';
                                    const actorDisplay = log.actorFullName || log.actorUsername;
                                    const showActor = actorDisplay && log.actorUsername !== log.targetUsername;

                                    return (
                                        <tr key={log.id} className="hover:bg-gray-50/70 dark:hover:bg-[#111214] transition-colors">
                                            <td className="px-5 py-3.5 whitespace-nowrap">
                                                <div className="flex items-center text-xs font-mono text-gray-800 dark:text-gray-200">
                                                    <Clock className="w-3.5 h-3.5 mr-1.5 text-gray-400 shrink-0" />
                                                    {new Date(log.occurredAt).toLocaleString('tr-TR')}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 whitespace-nowrap">
                                                <span className={`inline-block px-2.5 py-1 rounded-lg text-[11px] font-bold border ${getEventBadge(log.eventType)}`}>
                                                    {getEventLabel(log.eventType)}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="text-xs font-bold text-gray-900 dark:text-white">
                                                            {targetDisplay}
                                                        </span>
                                                        {log.targetFullName && log.targetUsername && log.targetFullName !== log.targetUsername && (
                                                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                                                {log.targetUsername}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {showActor && (
                                                        <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium mt-0.5">
                                                            İşlemi Yapan: {actorDisplay}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 text-xs text-gray-600 dark:text-gray-400">
                                                <div className="flex flex-col">
                                                    <span className="font-mono text-[11px]">{log.ipAddress || 'Bilinmiyor'}</span>
                                                    <span className="truncate max-w-[160px] text-[10px] text-gray-400" title={log.userAgent}>
                                                        {log.userAgent || '-'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <div className="text-xs text-gray-600 dark:text-gray-300 break-words">
                                                    {renderLogDetails(log)}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 whitespace-nowrap">
                                                {log.isSuccess ? (
                                                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs font-semibold">
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                        Başarılı
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 text-xs font-semibold">
                                                        <XCircle className="w-3.5 h-3.5" />
                                                        Hatalı
                                                    </span>
                                                )}
                                            </td>
                                            {hasUnlockPermission && (
                                                <td className="px-5 py-3.5 text-right whitespace-nowrap">
                                                    {log.eventType === 'LOCKOUT' ? (
                                                        log.isTargetUserLocked ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => setSelectedUserForUnlock({
                                                                    userId: log.targetUserId,
                                                                    targetUsername: log.targetUsername,
                                                                    targetFullName: log.targetFullName
                                                                })}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20 rounded-xl text-xs font-semibold transition-colors border border-emerald-200 dark:border-emerald-500/30"
                                                            >
                                                                <Unlock className="w-3.5 h-3.5" />
                                                                Kilidi Aç
                                                            </button>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 rounded-lg text-[11px] font-medium border border-gray-200 dark:border-gray-700">
                                                                <CheckCircle2 className="w-3 h-3" />
                                                                Kilidi Açık
                                                            </span>
                                                        )
                                                    ) : null}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Sayfalama (Pagination) */}
            {!loading && totalCount > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-[#18191c] p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                        Toplam <span className="font-bold text-gray-900 dark:text-white">{totalCount}</span> kayıt içinden{' '}
                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                            {(page - 1) * 20 + 1} - {Math.min(page * 20, totalCount)}
                        </span>{' '}
                        arası gösteriliyor
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-300"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 px-3">
                            Sayfa {page} / {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-300"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Kilit Açma Modalı */}
            {selectedUserForUnlock && (
                <UnlockConfirmModal
                    user={selectedUserForUnlock}
                    onClose={() => setSelectedUserForUnlock(null)}
                    onSuccess={() => {
                        setSelectedUserForUnlock(null);
                        fetchLogs();
                    }}
                />
            )}
        </div>
    );
};

export default SecurityAuditLogsPage;
